/**
 * Life Blueprint V2.9.9 — Production Core Adapter (V1 Task 1)
 * ─────────────────────────────────────────────────────────────────────────────
 * Production-safe orchestrator over the ALREADY-VALIDATED deterministic
 * V2.9.9 engine modules. It replaces the previous NOT_IMPLEMENTED body of
 * `runCoreRuleEngineV299` in `engine/coreRuleEngine.js`.
 *
 * STRICT INVARIANTS
 *   - Uses ONLY validated V2.9.9 engine modules (M02 references, M03
 *     batches 01–07, Batch 07-2 Sheet27 closure, M04 minimum Sheet28).
 *   - Does NOT import `poc_composer` (validation/evidence composer).
 *   - Does NOT import `validation/**`, `devHooks`, or GM fixtures.
 *   - Does NOT introduce or infer any new formula, token, or reference row.
 *   - Deterministic. Same input ⇒ byte-identical output. No wall clock,
 *     no randomness, no LLM.
 *   - Fails closed with UNSUPPORTED_PRODUCTION_INPUT when the caller has
 *     not supplied the audited upstream Golden-Master cells; M01
 *     preprocessing (birth-datetime → audited cells) is NOT MIGRATED and
 *     the adapter refuses to fabricate it.
 *
 * INPUT CONTRACT
 *   The adapter accepts the existing production `NormalizedInput`
 *   (`{name, birthDateISO, birthTimeSlot, gender, region, rawKey}`) plus an
 *   optional additive `audited` bundle carrying the V2.9.9 upstream cells
 *   that each accepted M03 batch requires as Golden-Master evidence:
 *
 *     upstreamYear_B5              M03 Batch 01
 *     upstreamDateTime_B13         M03 Batch 01/02
 *     upstreamBoundaryDate_E20     M03 Batch 01
 *     upstreamBoundaries_E19_E30   M03 Batch 02
 *     upstreamC32                  M03 Batch 03/04/05/06/07
 *     upstreamValue_B14            M03 Batch 04
 *     upstreamValue_B8             M03 Batch 05
 *     upstreamValue_F20            M03 Batch 07
 *     upstreamRange_F19_F30        M03 Batch 07
 *
 *   The pass-through of `raw.audited → normalized.audited` is a minimal
 *   backward-compatible extension of `normalize.js`; existing callers
 *   that do not pass `audited` continue to work through the mock path.
 *
 * OUTPUT CONTRACT (production CoreOutput; superset of the legacy shape)
 *   { source: "VALIDATED_V299",
 *     engineVersion: "V2.9.9",
 *     referenceDataVersion: "M02-B01/B02/B03/B04/B05/B06/B07-1",
 *     goldenMasterHash: <sha256 pin>,
 *     sourceStatus: "VALIDATED_V299",
 *     productionConnected: true,
 *     uiConnected: false,
 *     inputHash: normalized.rawKey,
 *     fourPillars, fullBazi, boundaryWarnings, boundaryWarning,
 *     stemBranchElements, yinYang, hiddenStems, hiddenWeights,
 *     tenGods, naYin, kongWang,
 *     fiveElementTotals, fiveElementRatios,
 *     seasonSupport, groundSupport, peerSupport,
 *     strengthScore, dayMasterElement, dayMasterRatio, dayMasterStatus,
 *     neededElement, tieWarning,
 *     pillars, rawFactors, mockVariantId,     ← legacy compat fields
 *     _provenance                              ← intermediate batch outputs
 *   }
 *   Any output field not backed by an accepted V2.9.9 batch is INTENTIONALLY
 *   absent — do not fabricate.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import {
  DB_TIAN_GAN,
  DB_DI_ZHI,
  tianGanWuXing,
  tianGanYinYang,
  diZhiBenQiWuXing,
  diZhiYinYang,
} from "./m02_batch01_reference";
import {
  DB_LIU_SHI_JIA_ZI,
  liuShiJiaZiLookup,
} from "./m02_batch04_reference";
import {
  computeSheet27B90,
  composeSheet27B82ThroughE84Column,
} from "./m02_batch06_reference";
import { computeBatch01Sheet27 } from "./batch01_sheet27";
import { computeBatch02Sheet27 } from "./batch02_sheet27";
import { computeBatch03Sheet27 } from "./batch03_sheet27";
import {
  PARTIAL_SHEET39_REFERENCE_EVIDENCE,
  computeBatch04Sheet27,
} from "./batch04_sheet27";
import { computeBatch05Sheet27 } from "./batch05_sheet27";
import { computeBatch06Sheet27 } from "./batch06_sheet27";
import { computeBatch07Sheet27Production } from "./productionBatch07Adapter";
import {
  computeSheet27B85ThroughE88 as _tenGodsSheet27,
} from "./batch07_2_sheet27";
import {
  computeSheet28Min as _sheet28Min,
} from "./m04_batch07_2_sheet28";

// ── Provenance pins (audited constants — do not modify) ─────────────
export const PRODUCTION_ADAPTER_ENGINE_VERSION = "V2.9.9";
export const PRODUCTION_ADAPTER_REFERENCE_DATA_VERSION =
  "M02-B01/B02/B03/B04/B05/B06/B07-1";
export const PRODUCTION_ADAPTER_GOLDEN_MASTER_HASH =
  "6d97148d708a3dbc84bcec9bdce9facdb815891de077036c9fc118a75e86d500";

export const PRODUCTION_BATCH03_REFERENCE = Object.freeze({
  Sheet40: Object.freeze({
    codeName: DB_TIAN_GAN.codeName,
    worksheetIndex: DB_TIAN_GAN.worksheetIndex,
    status: DB_TIAN_GAN.status,
    rows: Object.freeze(
      DB_TIAN_GAN.rows.map((row) =>
        Object.freeze({ a: row.xuHao, b: row.tianGan })
      )
    ),
  }),
  Sheet41: Object.freeze({
    codeName: DB_DI_ZHI.codeName,
    worksheetIndex: DB_DI_ZHI.worksheetIndex,
    status: DB_DI_ZHI.status,
    rows: Object.freeze(
      DB_DI_ZHI.rows.map((row) =>
        Object.freeze({ a: row.xuHao, b: row.diZhi })
      )
    ),
  }),
});

export const PRODUCTION_BATCH04_SHEET39_REFERENCE = Object.freeze({
  codeName: DB_LIU_SHI_JIA_ZI.codeName,
  worksheetIndex: DB_LIU_SHI_JIA_ZI.worksheetIndex,
  status: DB_LIU_SHI_JIA_ZI.status,

  // B51 depends on the separately audited Sheet39 support cells.
  // Do not infer these values from the 60-row table.
  cells: PARTIAL_SHEET39_REFERENCE_EVIDENCE.cells,

  // Batch04 VLOOKUPs use columns A/C/D/E of Sheet39.
  rows: Object.freeze(
    DB_LIU_SHI_JIA_ZI.rows.map((row) =>
      Object.freeze({
        a: row.xuHao,
        c: row.tianGan,
        d: row.tianGanIndex,
        e: row.diZhi,
      })
    )
  ),
});

// Audited pillar labels — code-point authoritative.
const LABEL_NIAN = String.fromCodePoint(24180, 26609); // 年柱
const LABEL_YUE  = String.fromCodePoint(26376, 26609); // 月柱
const LABEL_RI   = String.fromCodePoint(26085, 26609); // 日柱
const LABEL_SHI  = String.fromCodePoint(26102, 26609); // 时柱

/** Symbolic fail-closed marker consumed by the production-core gate. */
export const UNSUPPORTED_PRODUCTION_INPUT = "UNSUPPORTED_PRODUCTION_INPUT";

function requireField(bag, key, kindHint) {
  if (!Object.prototype.hasOwnProperty.call(bag, key)) {
    throw new Error(
      `[CoreRuleEngine·V2.9.9] ${UNSUPPORTED_PRODUCTION_INPUT}: ` +
        `normalized.audited.${key} is required (${kindHint}). ` +
        `No inference performed.`
    );
  }
  return bag[key];
}

/**
 * Production adapter body.
 *
 * @param {import('../schema').NormalizedInput & { audited?: object }} normalized
 * @param {typeof import('../referenceData').referenceData} _referenceData
 * @returns {object} production CoreOutput (frozen)
 */
export function runCoreRuleV299Production(normalized /*, _referenceData */) {
  if (normalized === null || typeof normalized !== "object") {
    throw new Error(
      `[CoreRuleEngine·V2.9.9] ${UNSUPPORTED_PRODUCTION_INPUT}: ` +
        `normalized input object is required.`
    );
  }
  const audited = normalized.audited;
  if (audited === null || typeof audited !== "object") {
    throw new Error(
      `[CoreRuleEngine·V2.9.9] ${UNSUPPORTED_PRODUCTION_INPUT}: ` +
        `normalized.audited bundle is required. The V2.9.9 slice requires ` +
        `audited upstream Golden-Master cells (B5, B8, B13, C32, E19..E30, ` +
        `F20, F19..F30, B14) produced by M01 preprocessing which is NOT ` +
        `MIGRATED. Fabrication is not permitted.`
    );
  }

  const upstreamYear_B5           = requireField(audited, "upstreamYear_B5",           "number");
  const upstreamDateTime_B13      = requireField(audited, "upstreamDateTime_B13",      "Date");
  const upstreamBoundaryDate_E20  = requireField(audited, "upstreamBoundaryDate_E20",  "Date");
  const upstreamBoundaries_E19_E30= requireField(audited, "upstreamBoundaries_E19_E30","object|array");
  const upstreamC32               = requireField(audited, "upstreamC32",               "string");
  const upstreamValue_B14         = requireField(audited, "upstreamValue_B14",         "number");
  const upstreamValue_B8          = requireField(audited, "upstreamValue_B8",          "number");
  const upstreamValue_F20         = requireField(audited, "upstreamValue_F20",         "string");
  const upstreamRange_F19_F30     = requireField(audited, "upstreamRange_F19_F30",     "string[]");

  // ── Validated DAG (same call graph the accepted per-batch GM
  //    fixtures exercise) ────────────────────────────────────────────
  const b01 = computeBatch01Sheet27({
    upstreamYear_B5,
    upstreamDateTime_B13,
    upstreamBoundaryDate_E20,
  });
  const b02 = computeBatch02Sheet27({
    upstreamDateTime_B13,
    upstreamBoundaries_E19_E30,
    upstreamValue_B34: b01.B34,
  });
  const b03 = computeBatch03Sheet27({
    upstreamC32,
    upstreamValue_B44: b02.B44,
    upstreamValue_B42: b02.B42,
    reference: PRODUCTION_BATCH03_REFERENCE,
  });
  const b04 = computeBatch04Sheet27({
    upstreamC32,
    upstreamValue_B14,
    sheet39Ref: PRODUCTION_BATCH04_SHEET39_REFERENCE,
  });
  const b05 = computeBatch05Sheet27({
    upstreamC32,
    upstreamValue_B8,
    upstreamValue_B53: b04.B53,
    reference: PRODUCTION_BATCH03_REFERENCE,
  });
  const b06 = computeBatch06Sheet27({
    upstreamC32,
    upstreamValue_B34: b01.B34,
    upstreamValue_B35: b01.B35,
    upstreamValue_B47: b03.B47,
    upstreamValue_B55: b04.B55,
    upstreamValue_B63: b05.B63,
    reference: PRODUCTION_BATCH03_REFERENCE,
  });
  const b07 = computeBatch07Sheet27Production({
    upstreamC32,
    upstreamValue_F20,
    upstreamRange_F19_F30,
    upstreamValue_B36: b06.B36,
    upstreamValue_B37: b06.B37,
    upstreamValue_B38: b06.B38,
    upstreamValue_B51: b04.B51,
    upstreamValue_B52: b04.B52,
  });
  const tenGodsMatrix = _tenGodsSheet27({
    dayStem:    b04.B52,
    yearStem:   b06.B36, yearBranch:  b06.B37,
    monthStem:  b03.B45, monthBranch: b03.B46,
    dayBranch:  b04.B54,
    hourStem:   b05.B61, hourBranch:  b05.B62,
  });
  const sheet28 = _sheet28Min({
    yearStem:   b06.B36, yearBranch:  b06.B37,
    monthStem:  b03.B45, monthBranch: b03.B46,
    dayStem:    b04.B52, dayBranch:   b04.B54,
    hourStem:   b05.B61, hourBranch:  b05.B62,
    boundarySummary: b07.B71,
  });

  // ── Deterministic output mapping ────────────────────────────────
  const fourPillars = Object.freeze([
    Object.freeze({ label: LABEL_NIAN, ganZhi: b06.B38, stem: b06.B36, branch: b06.B37 }),
    Object.freeze({ label: LABEL_YUE,  ganZhi: b03.B47, stem: b03.B45, branch: b03.B46 }),
    Object.freeze({ label: LABEL_RI,   ganZhi: b04.B55, stem: b04.B52, branch: b04.B54 }),
    Object.freeze({ label: LABEL_SHI,  ganZhi: b05.B63, stem: b05.B61, branch: b05.B62 }),
  ]);
  const fullBazi = b06.B70;
  const boundaryWarnings = Object.freeze({
    B39: b07.B39,
    B48: b07.B48,
    B71: b07.B71,
  });
  const stemBranchElements = Object.freeze(
    fourPillars.map((p) =>
      Object.freeze({
        stem: p.stem,
        stemElement: tianGanWuXing(p.stem),
        branch: p.branch,
        branchElement: diZhiBenQiWuXing(p.branch),
      })
    )
  );
  const yinYang = Object.freeze(
    fourPillars.map((p) =>
      Object.freeze({
        stem: p.stem,
        stemYinYang: tianGanYinYang(p.stem),
        branch: p.branch,
        branchYinYang: diZhiYinYang(p.branch),
      })
    )
  );
  const hiddenRows = sheet28.weightedRows.slice(4);
  const hiddenStems   = Object.freeze(hiddenRows.map((r) => r.B));
  const hiddenWeights = Object.freeze(hiddenRows.map((r) => r.D));
  const naYin = Object.freeze(
    fourPillars.map((p) =>
      Object.freeze({ ganZhi: p.ganZhi, naYin: liuShiJiaZiLookup(p.ganZhi) })
    )
  );
  const kongWang = computeSheet27B90({ b37: b06.B37, b51: b04.B51 });

  return Object.freeze({
    // Production provenance / engine metadata
    source: "VALIDATED_V299",
    engineVersion: PRODUCTION_ADAPTER_ENGINE_VERSION,
    referenceDataVersion: PRODUCTION_ADAPTER_REFERENCE_DATA_VERSION,
    goldenMasterHash: PRODUCTION_ADAPTER_GOLDEN_MASTER_HASH,
    sourceStatus: "VALIDATED_V299",
    productionConnected: true,
    uiConnected: false,
    inputHash: normalized.rawKey,
    // Structured V2.9.9 core outputs
    fourPillars,
    fullBazi,
    boundaryWarnings,
    boundaryWarning: sheet28.boundary,
    stemBranchElements,
    yinYang,
    hiddenStems,
    hiddenWeights,
    tenGods: Object.freeze({ ...tenGodsMatrix }),
    naYin,
    kongWang,
    fiveElementTotals: sheet28.elementTotals,
    fiveElementRatios: sheet28.elementRatios,
    seasonSupport: sheet28.supportFlags.B42,
    groundSupport: sheet28.supportFlags.B43,
    peerSupport:   sheet28.supportFlags.B44,
    strengthScore: sheet28.strengthScore,
    dayMasterElement: sheet28.dayMasterElement,
    dayMasterRatio:   sheet28.dayMasterRatio,
    dayMasterStatus:  sheet28.status,
    neededElement:    sheet28.need,
    tieWarning:       sheet28.tie,
    // Legacy CoreOutput compat fields (kept for downstream layers).
    pillars: Object.freeze({}),
    rawFactors: Object.freeze({
      elementTotals: sheet28.elementTotals,
      elementRatios: sheet28.elementRatios,
      strengthScore: sheet28.strengthScore,
      status:        sheet28.status,
    }),
    mockVariantId: null,
    // Auditable intermediate batch outputs.
    _provenance: Object.freeze({
      adapter: "engine/v299/productionCoreAdapter",
      wiredIntoProductionCorePipeline: true,
      wiredIntoAnalysisEngine: false,
      wiredIntoInterpretationEngine: false,
      wiredIntoUi: false,
      intermediate: Object.freeze({
        batch01: b01, batch02: b02, batch03: b03, batch04: b04,
        batch05: b05, batch06: b06, batch07: b07,
      }),
      composedReferences: Object.freeze({
        b82_year:  composeSheet27B82ThroughE84Column(b06.B37),
        b82_month: composeSheet27B82ThroughE84Column(b03.B46),
        b82_day:   composeSheet27B82ThroughE84Column(b04.B54),
        b82_hour:  composeSheet27B82ThroughE84Column(b05.B62),
      }),
    }),
  });
}
